import React, { useState, useEffect, useMemo, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Receipt, Plus, Search, Filter, Check, X, Send, Trash2, Eye,
  Wallet, FolderKanban, BarChart3, RefreshCw, FileText, DollarSign,
  Clock, AlertCircle, CheckCircle2, XCircle, Pencil, ChevronDown,
  Paperclip, Upload, Download, FileBarChart,
} from 'lucide-react';
import { RecurringExpensesView } from '@/components/RecurringExpensesView';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '@/components/ui/dialog';
import { Switch } from '@/components/ui/switch';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import { DateInput } from '@/components/ui/date-input';

const API_BASE_URL = (process.env.REACT_APP_BACKEND_URL || '') + '/api';

// ─── shared helpers ────────────────────────────────────────────────────────
const fmtMoney = (v, currency = 'AED') => {
  const n = Number(v || 0);
  return `${currency} ${n.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
};
const fmtDate = (iso) => (iso ? String(iso).slice(0, 10) : '—');
const STATUS_TONE = {
  draft: 'bg-slate-500/20 text-slate-300',
  submitted: 'bg-amber-500/20 text-amber-300',
  approved: 'bg-emerald-500/20 text-emerald-300',
  rejected: 'bg-rose-500/20 text-rose-300',
  pending_reimbursement: 'bg-blue-500/20 text-blue-300',
  settled: 'bg-emerald-600/30 text-emerald-200',
  cancelled: 'bg-zinc-500/20 text-zinc-400',
};
const StatusPill = ({ status }) => (
  <span
    className={`inline-block px-2 py-0.5 rounded text-xs ${STATUS_TONE[status] || 'bg-slate-500/20 text-slate-300'}`}
    data-testid={`expense-status-${status}`}
  >
    {(status || '—').replace(/_/g, ' ')}
  </span>
);

const authHeaders = () => ({
  Authorization: `Bearer ${localStorage.getItem('token')}`,
});

// ─── Audit timeline shared by detail drawers ──────────────────────────────
const ACTION_META = {
  created:             { tone: 'bg-slate-500/20 text-slate-200',   label: 'Created' },
  updated:             { tone: 'bg-blue-500/20 text-blue-200',     label: 'Updated' },
  submitted:           { tone: 'bg-amber-500/20 text-amber-200',   label: 'Submitted' },
  approved:            { tone: 'bg-emerald-500/20 text-emerald-200', label: 'Approved' },
  rejected:            { tone: 'bg-rose-500/20 text-rose-200',     label: 'Rejected' },
  settled:             { tone: 'bg-emerald-600/30 text-emerald-100', label: 'Settled' },
  bulk_settled:        { tone: 'bg-emerald-600/30 text-emerald-100', label: 'Settled (bulk)' },
  cancelled:           { tone: 'bg-zinc-500/20 text-zinc-300',     label: 'Cancelled' },
  attachment_added:    { tone: 'bg-violet-500/20 text-violet-200', label: 'Receipt added' },
  attachment_removed:  { tone: 'bg-violet-500/20 text-violet-200', label: 'Receipt removed' },
  deleted:             { tone: 'bg-rose-500/20 text-rose-200',     label: 'Deleted' },
};

const _initials = (name) => {
  if (!name) return '?';
  const parts = String(name).trim().split(/\s+/);
  return ((parts[0]?.[0] || '') + (parts[1]?.[0] || '')).toUpperCase() || '?';
};

const _fmtAuditTime = (iso) => {
  if (!iso) return '—';
  const d = new Date(iso);
  if (isNaN(d.getTime())) return iso;
  return d.toLocaleString(undefined, { month: 'short', day: '2-digit', hour: '2-digit', minute: '2-digit' });
};

const _renderDetails = (action, details) => {
  if (!details) return null;
  if (action === 'updated') {
    // Show changed fields except meta (updated_at)
    const fields = Object.entries(details).filter(([k]) => k !== 'updated_at');
    if (fields.length === 0) return null;
    return (
      <div className="mt-1 text-[11px] text-wt-text-muted space-y-0.5" data-testid="audit-diff">
        {fields.map(([k, v]) => (
          <div key={k}>
            <span className="text-wt-text-secondary">{k.replace(/_/g, ' ')}</span>: <span className="font-mono">{String(v).slice(0, 80)}</span>
          </div>
        ))}
      </div>
    );
  }
  if (action === 'rejected' && details.reason) {
    return <div className="mt-1 text-[11px] text-rose-300 italic">"{details.reason}"</div>;
  }
  if (action === 'settled' || action === 'bulk_settled') {
    return (
      <div className="mt-1 text-[11px] text-wt-text-muted">
        {details.reference ? <>Ref: <span className="font-mono">{details.reference}</span> · </> : null}
        Payment mode: <span className="font-mono">{(details.payment_mode_id || '').slice(0, 8)}…</span>
      </div>
    );
  }
  if (action === 'attachment_added' || action === 'attachment_removed') {
    return details.name ? <div className="mt-1 text-[11px] text-wt-text-muted">{details.name}</div> : null;
  }
  return null;
};

export const AuditTimeline = ({ entries }) => (
  <ol className="space-y-3" data-testid="audit-timeline">
    {entries.map((a, idx) => {
      const meta = ACTION_META[a.action] || { tone: 'bg-slate-500/20 text-slate-300', label: a.action };
      const isLast = idx === entries.length - 1;
      return (
        <li key={a.id} className="relative flex gap-3" data-testid={`audit-entry-${a.action}`}>
          <div className="flex flex-col items-center">
            <div className="w-7 h-7 rounded-full bg-wt-gold/15 border border-wt-gold/40 flex items-center justify-center text-[10px] font-semibold text-wt-gold">
              {_initials(a.actor_name)}
            </div>
            {!isLast && <div className="flex-1 w-px bg-wt-border my-1" />}
          </div>
          <div className="flex-1 pb-1">
            <div className="flex items-center flex-wrap gap-2 text-xs">
              <span className={`px-2 py-0.5 rounded ${meta.tone}`}>{meta.label}</span>
              <span className="text-wt-text-secondary">by {a.actor_name || a.actor_id || 'system'}</span>
              <span className="text-wt-text-muted font-mono text-[10px] ml-auto">{_fmtAuditTime(a.created_at)}</span>
            </div>
            {_renderDetails(a.action, a.details)}
          </div>
        </li>
      );
    })}
  </ol>
);

// ─── Tabbed shell ──────────────────────────────────────────────────────────
const TABS = [
  { id: 'dashboard', label: 'Dashboard', icon: BarChart3 },
  { id: 'all', label: 'All Expenses', icon: Receipt },
  { id: 'recurring', label: 'Recurring', icon: RefreshCw },
  { id: 'reimbursements', label: 'Reimbursements', icon: DollarSign },
  { id: 'reports', label: 'Reports', icon: FileBarChart },
  { id: 'categories', label: 'Categories', icon: FolderKanban },
  { id: 'payment-modes', label: 'Payment Modes', icon: Wallet },
];

export default function ExpensesPage() {
  const [tab, setTab] = useState(() => {
    return new URLSearchParams(window.location.search).get('tab') || 'dashboard';
  });
  const onTabChange = (id) => {
    setTab(id);
    const url = new URL(window.location.href);
    url.searchParams.set('tab', id);
    window.history.replaceState({}, '', url);
  };

  return (
    <div className="space-y-6" data-testid="expenses-page">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div>
          <h1 className="font-playfair text-2xl sm:text-3xl md:text-4xl font-bold text-wt-text-primary flex items-center gap-2">
            <Receipt className="w-6 h-6 text-wt-gold" />
            Expenses
          </h1>
          <p className="text-wt-text-secondary text-sm">
            Track company spend and employee reimbursements
          </p>
        </div>
      </div>

      <div className="flex flex-wrap gap-2 border-b border-wt-border pb-2">
        {TABS.map(({ id, label, icon: Icon }) => (
          <button
            key={id}
            data-testid={`expenses-tab-${id}`}
            onClick={() => onTabChange(id)}
            className={`inline-flex items-center gap-2 px-3 py-2 rounded-lg text-sm transition-colors ${
              tab === id
                ? 'bg-wt-gold/10 text-wt-gold border border-wt-gold/40'
                : 'text-wt-text-secondary hover:bg-wt-paper'
            }`}
          >
            <Icon className="w-4 h-4" />
            {label}
          </button>
        ))}
      </div>

      {tab === 'dashboard' && <DashboardView />}
      {tab === 'all' && <AllExpensesView />}
      {tab === 'recurring' && <RecurringExpensesView />}
      {tab === 'reimbursements' && <ReimbursementsView />}
      {tab === 'reports' && <ReportsView />}
      {tab === 'categories' && <CategoriesView />}
      {tab === 'payment-modes' && <PaymentModesView />}
    </div>
  );
}

// ────────────────────────────  Dashboard  ──────────────────────────────────
const DashboardView = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API_BASE_URL}/expenses/dashboard/summary`, { headers: authHeaders() });
      setData(r.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load dashboard');
    } finally {
      setLoading(false);
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  const cards = useMemo(() => {
    if (!data) return [];
    return [
      { label: 'This Month', total: data.this_month?.total, count: data.this_month?.count, icon: Receipt, tone: 'text-emerald-300' },
      { label: 'Pending Approval', total: data.pending?.total, count: data.pending?.count, icon: Clock, tone: 'text-amber-300' },
      { label: 'Approved', total: data.approved?.total, count: data.approved?.count, icon: CheckCircle2, tone: 'text-emerald-300' },
      { label: 'Rejected', total: data.rejected?.total, count: data.rejected?.count, icon: XCircle, tone: 'text-rose-300' },
      { label: 'Reimbursements Pending', total: data.reimbursements_pending?.total, count: data.reimbursements_pending?.count, icon: DollarSign, tone: 'text-blue-300' },
      { label: 'Reimbursements Paid', total: data.reimbursements_paid?.total, count: data.reimbursements_paid?.count, icon: CheckCircle2, tone: 'text-emerald-300' },
      { label: 'Company Paid', total: data.company_paid?.total, count: data.company_paid?.count, icon: Wallet, tone: 'text-violet-300' },
    ];
  }, [data]);

  return (
    <div className="space-y-6" data-testid="expenses-dashboard">
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
        {loading ? (
          <div className="col-span-full text-wt-text-muted text-sm">Loading…</div>
        ) : cards.map((c, i) => (
          <div key={i} className="bg-wt-paper border border-wt-border rounded-xl p-4">
            <div className="flex items-center justify-between">
              <c.icon className={`w-5 h-5 ${c.tone}`} />
              <span className="text-xs text-wt-text-muted">{c.count || 0} item{c.count === 1 ? '' : 's'}</span>
            </div>
            <div className="mt-3 text-wt-text-muted text-xs uppercase tracking-wide">{c.label}</div>
            <div className="mt-1 text-xl font-semibold text-wt-text-primary">{fmtMoney(c.total)}</div>
          </div>
        ))}
      </div>

      <div className="bg-wt-paper border border-wt-border rounded-xl p-4">
        <h3 className="text-sm font-semibold text-wt-text-primary mb-3">Category Breakdown (this month)</h3>
        {(data?.category_breakdown || []).length === 0 ? (
          <p className="text-xs text-wt-text-muted">No approved expenses this month yet.</p>
        ) : (
          <div className="space-y-2" data-testid="dashboard-category-breakdown">
            {data.category_breakdown.map((c) => (
              <div key={c.category_id} className="flex items-center justify-between text-sm">
                <span className="text-wt-text-secondary">{c.category_name}</span>
                <span className="text-wt-text-primary font-mono">{fmtMoney(c.total)} <span className="text-wt-text-muted text-xs">· {c.count}</span></span>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

// ────────────────────────────  All Expenses  ───────────────────────────────
const AllExpensesView = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filters, setFilters] = useState({ search: '', status: '', category_id: '', paid_by: '' });
  const [categories, setCategories] = useState([]);
  const [paymentModes, setPaymentModes] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [detailExpense, setDetailExpense] = useState(null);
  const [actionState, setActionState] = useState(null); // {type, expense}

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (filters.search) params.search = filters.search;
      if (filters.status) params.status = filters.status;
      if (filters.category_id) params.category_id = filters.category_id;
      if (filters.paid_by) params.paid_by = filters.paid_by;
      const r = await axios.get(`${API_BASE_URL}/expenses`, { params, headers: authHeaders() });
      setItems(r.data.items || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load expenses');
    } finally {
      setLoading(false);
    }
  }, [filters]);

  const loadAux = useCallback(async () => {
    try {
      const [c, p] = await Promise.all([
        axios.get(`${API_BASE_URL}/expenses/categories`, { headers: authHeaders() }),
        axios.get(`${API_BASE_URL}/expenses/payment-modes`, { headers: authHeaders() }),
      ]);
      setCategories(c.data.categories || []);
      setPaymentModes(p.data.payment_modes || []);
    } catch (_e) { /* noop */ }
  }, []);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { loadAux(); }, [loadAux]);

  const openCreate = () => { setEditing(null); setShowForm(true); };
  const openEdit = (exp) => { setEditing(exp); setShowForm(true); };

  return (
    <div className="space-y-4" data-testid="all-expenses-view">
      {/* Toolbar */}
      <div className="bg-wt-paper border border-wt-border rounded-xl p-3 flex flex-wrap items-center gap-2">
        <div className="relative flex-1 min-w-[220px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-wt-text-muted" />
          <Input
            placeholder="Search title, vendor, invoice, order…"
            value={filters.search}
            onChange={(e) => setFilters({ ...filters, search: e.target.value })}
            className="pl-9"
            data-testid="expenses-search"
          />
        </div>
        <Select value={filters.status || 'all'} onValueChange={(v) => setFilters({ ...filters, status: v === 'all' ? '' : v })}>
          <SelectTrigger className="w-[160px]" data-testid="filter-status"><SelectValue placeholder="Status" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All statuses</SelectItem>
            {['draft','submitted','approved','rejected','pending_reimbursement','settled','cancelled'].map(s => (
              <SelectItem key={s} value={s}>{s.replace(/_/g, ' ')}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select value={filters.category_id || 'all'} onValueChange={(v) => setFilters({ ...filters, category_id: v === 'all' ? '' : v })}>
          <SelectTrigger className="w-[180px]" data-testid="filter-category"><SelectValue placeholder="Category" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All categories</SelectItem>
            {categories.map(c => (<SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>))}
          </SelectContent>
        </Select>
        <Select value={filters.paid_by || 'all'} onValueChange={(v) => setFilters({ ...filters, paid_by: v === 'all' ? '' : v })}>
          <SelectTrigger className="w-[140px]" data-testid="filter-paid-by"><SelectValue placeholder="Paid By" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Any</SelectItem>
            <SelectItem value="company">Company</SelectItem>
            <SelectItem value="employee">Employee</SelectItem>
          </SelectContent>
        </Select>
        <Button variant="outline" onClick={load} data-testid="refresh-expenses-btn">
          <RefreshCw className="w-4 h-4 mr-1" /> Refresh
        </Button>
        <Button onClick={openCreate} className="bg-wt-gold hover:bg-wt-gold/90 text-black" data-testid="create-expense-btn">
          <Plus className="w-4 h-4 mr-1" /> New Expense
        </Button>
      </div>

      {/* Table */}
      <div className="bg-wt-paper border border-wt-border rounded-xl overflow-x-auto">
        <table className="w-full text-sm" data-testid="expenses-table">
          <thead>
            <tr className="text-left text-xs uppercase tracking-wide text-wt-text-muted border-b border-wt-border">
              <th className="px-4 py-3">Date</th>
              <th className="px-4 py-3">Title</th>
              <th className="px-4 py-3">Category</th>
              <th className="px-4 py-3 text-right">Amount</th>
              <th className="px-4 py-3">Paid By</th>
              <th className="px-4 py-3">Status</th>
              <th className="px-4 py-3">Requested By</th>
              <th className="px-4 py-3 text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={8} className="px-4 py-8 text-center text-wt-text-muted">Loading…</td></tr>
            ) : items.length === 0 ? (
              <tr><td colSpan={8} className="px-4 py-8 text-center text-wt-text-muted">No expenses yet — click <b>New Expense</b> to add one.</td></tr>
            ) : (
              items.map((e) => (
                <tr key={e.id} className="border-b border-wt-border last:border-0 hover:bg-wt-bg/40" data-testid={`expense-row-${e.id}`}>
                  <td className="px-4 py-3 text-wt-text-secondary">{fmtDate(e.expense_date)}</td>
                  <td className="px-4 py-3">
                    <button onClick={() => setDetailExpense(e)} className="text-wt-text-primary hover:text-wt-gold text-left" data-testid={`expense-title-${e.id}`}>
                      {e.title}
                    </button>
                    {e.vendor_name && <div className="text-xs text-wt-text-muted">{e.vendor_name}</div>}
                  </td>
                  <td className="px-4 py-3 text-wt-text-secondary">{e.category_name || '—'}</td>
                  <td className="px-4 py-3 text-right font-mono text-wt-text-primary">{fmtMoney(e.amount)}</td>
                  <td className="px-4 py-3 capitalize text-wt-text-secondary">{e.paid_by}</td>
                  <td className="px-4 py-3"><StatusPill status={e.status} /></td>
                  <td className="px-4 py-3 text-wt-text-secondary">{e.requested_by_name || '—'}</td>
                  <td className="px-4 py-3">
                    <div className="flex items-center justify-end gap-1">
                      <Button size="icon" variant="ghost" onClick={() => setDetailExpense(e)} data-testid={`view-expense-${e.id}`}><Eye className="w-4 h-4" /></Button>
                      {['draft','submitted'].includes(e.status) && (
                        <Button size="icon" variant="ghost" onClick={() => openEdit(e)} data-testid={`edit-expense-${e.id}`}><Pencil className="w-4 h-4" /></Button>
                      )}
                      {e.status === 'submitted' && (
                        <>
                          <Button size="icon" variant="ghost" onClick={() => setActionState({ type: 'approve', expense: e })} data-testid={`approve-expense-${e.id}`} className="text-emerald-400"><Check className="w-4 h-4" /></Button>
                          <Button size="icon" variant="ghost" onClick={() => setActionState({ type: 'reject', expense: e })} data-testid={`reject-expense-${e.id}`} className="text-rose-400"><X className="w-4 h-4" /></Button>
                        </>
                      )}
                      {['approved','pending_reimbursement'].includes(e.status) && e.paid_by === 'employee' && (
                        <Button size="icon" variant="ghost" onClick={() => setActionState({ type: 'settle', expense: e })} data-testid={`settle-expense-${e.id}`} className="text-blue-400"><Send className="w-4 h-4" /></Button>
                      )}
                      {!['settled','cancelled'].includes(e.status) && (
                        <Button size="icon" variant="ghost" onClick={() => setActionState({ type: 'cancel', expense: e })} data-testid={`cancel-expense-${e.id}`} className="text-rose-400"><Trash2 className="w-4 h-4" /></Button>
                      )}
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {showForm && (
        <ExpenseFormModal
          editing={editing}
          categories={categories}
          paymentModes={paymentModes}
          onClose={() => setShowForm(false)}
          onSaved={() => { setShowForm(false); load(); }}
        />
      )}

      {detailExpense && (
        <ExpenseDetailDrawer expense={detailExpense} onClose={() => setDetailExpense(null)} />
      )}

      {actionState && (
        <ExpenseActionModal
          action={actionState.type}
          expense={actionState.expense}
          paymentModes={paymentModes}
          onClose={() => setActionState(null)}
          onDone={() => { setActionState(null); load(); }}
        />
      )}
    </div>
  );
};

// ────────────────────────────  Form modal  ─────────────────────────────────
const ExpenseFormModal = ({ editing, categories, paymentModes, onClose, onSaved }) => {
  const [form, setForm] = useState(() => ({
    title: editing?.title || '',
    category_id: editing?.category_id || '',
    amount: editing?.amount || '',
    expense_date: editing?.expense_date || new Date().toISOString().slice(0, 10),
    paid_by: editing?.paid_by || 'company',
    payment_mode_id: editing?.payment_mode_id || '',
    description: editing?.description || '',
    vendor_name: editing?.vendor_name || '',
    invoice_number: editing?.invoice_number || '',
    auto_approve: false,
  }));
  const [saving, setSaving] = useState(false);

  const save = async () => {
    if (!form.title.trim()) { toast.error('Title is required'); return; }
    if (!form.category_id) { toast.error('Category is required'); return; }
    if (!Number(form.amount) || Number(form.amount) <= 0) { toast.error('Amount must be > 0'); return; }
    setSaving(true);
    try {
      const payload = {
        ...form,
        amount: Number(form.amount),
        category_id: form.category_id,
        payment_mode_id: form.payment_mode_id || null,
      };
      if (editing) {
        await axios.patch(`${API_BASE_URL}/expenses/${editing.id}`, payload, { headers: authHeaders() });
        toast.success('Expense updated');
      } else {
        await axios.post(`${API_BASE_URL}/expenses`, payload, { headers: authHeaders() });
        toast.success('Expense created');
      }
      onSaved();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl" data-testid="expense-form-modal">
        <DialogHeader><DialogTitle>{editing ? 'Edit Expense' : 'New Expense'}</DialogTitle></DialogHeader>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div className="sm:col-span-2">
            <Label>Title *</Label>
            <Input value={form.title} onChange={e => setForm({ ...form, title: e.target.value })} data-testid="form-title" />
          </div>
          <div>
            <Label>Category *</Label>
            <Select value={form.category_id} onValueChange={v => setForm({ ...form, category_id: v })}>
              <SelectTrigger data-testid="form-category"><SelectValue placeholder="Select category" /></SelectTrigger>
              <SelectContent>
                {categories.map(c => (<SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>Amount *</Label>
            <Input type="number" step="0.01" value={form.amount} onChange={e => setForm({ ...form, amount: e.target.value })} data-testid="form-amount" />
          </div>
          <div>
            <Label>Expense Date *</Label>
            <DateInput  value={form.expense_date} onChange={e => setForm({ ...form, expense_date: e.target.value })} data-testid="form-date" />
          </div>
          <div>
            <Label>Paid By *</Label>
            <Select value={form.paid_by} onValueChange={v => setForm({ ...form, paid_by: v })}>
              <SelectTrigger data-testid="form-paid-by"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="company">Company</SelectItem>
                <SelectItem value="employee">Employee</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="sm:col-span-2">
            <Label>Payment Mode {form.paid_by === 'company' ? '*' : '(optional)'}</Label>
            <Select value={form.payment_mode_id || ''} onValueChange={v => setForm({ ...form, payment_mode_id: v })}>
              <SelectTrigger data-testid="form-payment-mode"><SelectValue placeholder="Select payment mode" /></SelectTrigger>
              <SelectContent>
                {paymentModes.map(m => (<SelectItem key={m.id} value={m.id}>{m.name}</SelectItem>))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>Vendor (optional)</Label>
            <Input value={form.vendor_name} onChange={e => setForm({ ...form, vendor_name: e.target.value })} data-testid="form-vendor" />
          </div>
          <div>
            <Label>Invoice Number (optional)</Label>
            <Input value={form.invoice_number} onChange={e => setForm({ ...form, invoice_number: e.target.value })} data-testid="form-invoice" />
          </div>
          <div className="sm:col-span-2">
            <Label>Description / Notes</Label>
            <Textarea value={form.description} onChange={e => setForm({ ...form, description: e.target.value })} rows={3} data-testid="form-description" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={save} disabled={saving} className="bg-wt-gold hover:bg-wt-gold/90 text-black" data-testid="form-save-btn">
            {saving ? 'Saving…' : (editing ? 'Update' : 'Create')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// ────────────────────────────  Action modal  ───────────────────────────────
const ExpenseActionModal = ({ action, expense, paymentModes, onClose, onDone }) => {
  const [reason, setReason] = useState('');
  const [paymentModeId, setPaymentModeId] = useState('');
  const [reference, setReference] = useState('');
  const [notes, setNotes] = useState('');
  const [busy, setBusy] = useState(false);

  const titles = {
    approve: 'Approve Expense',
    reject: 'Reject Expense',
    settle: 'Settle Reimbursement',
    cancel: 'Cancel Expense',
  };

  const run = async () => {
    setBusy(true);
    try {
      if (action === 'approve') {
        await axios.post(`${API_BASE_URL}/expenses/${expense.id}/approve`, {}, { headers: authHeaders() });
        toast.success('Expense approved');
      } else if (action === 'reject') {
        if (!reason.trim()) { toast.error('Reason required'); setBusy(false); return; }
        await axios.post(`${API_BASE_URL}/expenses/${expense.id}/reject`, { reason }, { headers: authHeaders() });
        toast.success('Expense rejected');
      } else if (action === 'settle') {
        if (!paymentModeId) { toast.error('Payment mode required'); setBusy(false); return; }
        await axios.post(
          `${API_BASE_URL}/expenses/${expense.id}/settle`,
          { payment_mode_id: paymentModeId, reference, notes },
          { headers: authHeaders() },
        );
        toast.success('Settled — cash transaction posted');
      } else if (action === 'cancel') {
        await axios.post(`${API_BASE_URL}/expenses/${expense.id}/cancel`, { reason }, { headers: authHeaders() });
        toast.success('Expense cancelled');
      }
      onDone();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Action failed');
    } finally { setBusy(false); }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid={`action-modal-${action}`}>
        <DialogHeader><DialogTitle>{titles[action]}</DialogTitle></DialogHeader>
        <div className="space-y-3 text-sm">
          <div className="text-wt-text-secondary">
            <b>{expense.title}</b> — {fmtMoney(expense.amount)} ({expense.paid_by})
          </div>
          {action === 'reject' && (
            <div>
              <Label>Rejection reason *</Label>
              <Textarea value={reason} onChange={e => setReason(e.target.value)} rows={3} data-testid="reject-reason" />
            </div>
          )}
          {action === 'settle' && (
            <>
              <div>
                <Label>Payment mode *</Label>
                <Select value={paymentModeId} onValueChange={setPaymentModeId}>
                  <SelectTrigger data-testid="settle-payment-mode"><SelectValue placeholder="Select payment mode" /></SelectTrigger>
                  <SelectContent>
                    {paymentModes.map(m => (<SelectItem key={m.id} value={m.id}>{m.name}</SelectItem>))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Reference (optional)</Label>
                <Input value={reference} onChange={e => setReference(e.target.value)} data-testid="settle-reference" />
              </div>
              <div>
                <Label>Notes (optional)</Label>
                <Textarea value={notes} onChange={e => setNotes(e.target.value)} rows={2} data-testid="settle-notes" />
              </div>
            </>
          )}
          {action === 'cancel' && (
            <div>
              <Label>Reason (optional)</Label>
              <Textarea value={reason} onChange={e => setReason(e.target.value)} rows={2} data-testid="cancel-reason" />
            </div>
          )}
          {action === 'approve' && (
            <div className="text-wt-text-muted text-xs">
              {expense.paid_by === 'employee' ? (
                <>Approving will create a <b>payable entry</b> in the employee ledger. No cash leaves the company until you settle the reimbursement.</>
              ) : (
                <>Approving will post the <b>cash outflow</b> immediately against the chosen payment mode.</>
              )}
            </div>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={run} disabled={busy} className={action === 'reject' || action === 'cancel' ? 'bg-rose-500 hover:bg-rose-400' : 'bg-wt-gold hover:bg-wt-gold/90 text-black'} data-testid={`confirm-${action}-btn`}>
            {busy ? '…' : titles[action]}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// ────────────────────────────  Detail drawer  ──────────────────────────────
const ExpenseDetailDrawer = ({ expense, onClose }) => {
  const [audit, setAudit] = useState([]);
  const [exp, setExp] = useState(expense);
  const [uploading, setUploading] = useState(false);

  const refresh = useCallback(async () => {
    try {
      const r = await axios.get(`${API_BASE_URL}/expenses/${expense.id}`, { headers: authHeaders() });
      setExp(r.data);
    } catch (_e) { /* noop */ }
  }, [expense.id]);

  useEffect(() => {
    axios.get(`${API_BASE_URL}/expenses/${expense.id}/audit`, { headers: authHeaders() })
      .then(r => setAudit(r.data.entries || []))
      .catch(() => setAudit([]));
  }, [expense.id]);

  const uploadAttachment = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      await axios.post(`${API_BASE_URL}/expenses/${expense.id}/attachments`, fd, {
        headers: { ...authHeaders(), 'Content-Type': 'multipart/form-data' },
      });
      toast.success('Receipt attached');
      await refresh();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Upload failed');
    } finally { setUploading(false); }
  };

  const removeAttachment = async (att) => {
    if (!window.confirm(`Remove "${att.name}"?`)) return;
    try {
      await axios.delete(`${API_BASE_URL}/expenses/${expense.id}/attachments/${att.id}`, { headers: authHeaders() });
      toast.success('Removed');
      await refresh();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  const canEditAttachments = !['cancelled'].includes(exp.status);

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto" data-testid="expense-detail-drawer">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            {exp.title}
            <StatusPill status={exp.status} />
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-4 text-sm">
          <div className="grid grid-cols-2 gap-3">
            <DetailField label="Amount" value={fmtMoney(exp.amount)} />
            <DetailField label="Date" value={fmtDate(exp.expense_date)} />
            <DetailField label="Category" value={exp.category_name} />
            <DetailField label="P&L Group" value={exp.pnl_group} />
            <DetailField label="Paid By" value={exp.paid_by} />
            <DetailField label="Vendor" value={exp.vendor_name || '—'} />
            <DetailField label="Invoice #" value={exp.invoice_number || '—'} />
            <DetailField label="Requested By" value={exp.requested_by_name || '—'} />
            <DetailField label="Approved By" value={exp.approved_by_name || '—'} />
            <DetailField label="Approved At" value={fmtDate(exp.approved_at) || '—'} />
            {exp.status === 'rejected' && (
              <DetailField label="Rejection Reason" value={exp.rejection_reason} colSpan />
            )}
            {exp.status === 'settled' && (
              <>
                <DetailField label="Settled At" value={fmtDate(exp.settled_at)} />
                <DetailField label="Settlement Ref" value={exp.settlement_reference || '—'} />
              </>
            )}
          </div>
          {exp.description && (
            <div>
              <div className="text-xs uppercase text-wt-text-muted mb-1">Notes</div>
              <p className="text-wt-text-secondary">{exp.description}</p>
            </div>
          )}

          {/* Attachments */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <div className="text-xs uppercase text-wt-text-muted">Receipts ({(exp.attachments || []).length})</div>
              {canEditAttachments && (
                <label className="text-xs text-wt-gold cursor-pointer flex items-center gap-1" data-testid="detail-upload-label">
                  <Upload className="w-3 h-3" /> {uploading ? 'Uploading…' : 'Add receipt'}
                  <input
                    type="file"
                    className="hidden"
                    accept="image/jpeg,image/png,image/webp,image/heic,application/pdf"
                    onChange={e => uploadAttachment(e.target.files?.[0])}
                  />
                </label>
              )}
            </div>
            {(exp.attachments || []).length === 0 ? (
              <p className="text-xs text-wt-text-muted">No receipts attached.</p>
            ) : (
              <ul className="space-y-1" data-testid="detail-attachment-list">
                {exp.attachments.map(a => (
                  <li key={a.id} className="flex items-center justify-between text-xs bg-wt-bg/40 rounded px-2 py-1" data-testid={`detail-attachment-${a.id}`}>
                    <a href={`${process.env.REACT_APP_BACKEND_URL}${a.url}`} target="_blank" rel="noreferrer" className="flex items-center gap-1 hover:text-wt-gold">
                      <Paperclip className="w-3 h-3" /> {a.name}
                      <span className="text-wt-text-muted">· {(a.size / 1024).toFixed(1)} KB</span>
                    </a>
                    {canEditAttachments && (
                      <button onClick={() => removeAttachment(a)} className="text-rose-400 hover:text-rose-300">
                        <X className="w-3 h-3" />
                      </button>
                    )}
                  </li>
                ))}
              </ul>
            )}
          </div>

          <div>
            <div className="text-xs uppercase text-wt-text-muted mb-1">Financial Postings</div>
            <ul className="text-xs text-wt-text-secondary space-y-1 list-disc ml-5">
              <li>Employee ledger: {exp.ledger_entry_id ? `payable posted (${exp.ledger_entry_id.slice(0,8)}…)` : 'none'}</li>
              <li>Cash transaction: {exp.cash_transaction_id ? `posted (${exp.cash_transaction_id.slice(0,8)}…)` : 'none'}</li>
              <li>Settlement cash tx: {exp.settlement_cash_transaction_id ? `posted (${exp.settlement_cash_transaction_id.slice(0,8)}…)` : '—'}</li>
              <li>Settlement ledger entry: {exp.settlement_ledger_entry_id ? `posted (${exp.settlement_ledger_entry_id.slice(0,8)}…)` : '—'}</li>
            </ul>
          </div>
          <div>
            <div className="text-xs uppercase text-wt-text-muted mb-2">Audit Trail</div>
            {audit.length === 0 ? (
              <p className="text-xs text-wt-text-muted">No audit entries.</p>
            ) : (
              <AuditTimeline entries={audit} />
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
const DetailField = ({ label, value, colSpan }) => (
  <div className={colSpan ? 'col-span-2' : ''}>
    <div className="text-xs uppercase text-wt-text-muted">{label}</div>
    <div className="text-wt-text-primary capitalize">{value ?? '—'}</div>
  </div>
);

// ────────────────────────────  Reimbursements  ─────────────────────────────
const ReimbursementsView = () => {
  const [pending, setPending] = useState([]);
  const [loading, setLoading] = useState(false);
  const [paymentModes, setPaymentModes] = useState([]);
  const [actionExpense, setActionExpense] = useState(null);
  const [selected, setSelected] = useState({}); // id -> true
  const [showBulk, setShowBulk] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [r, pm] = await Promise.all([
        axios.get(`${API_BASE_URL}/expenses/reimbursements?settled=false`, { headers: authHeaders() }),
        axios.get(`${API_BASE_URL}/expenses/payment-modes`, { headers: authHeaders() }),
      ]);
      setPending(r.data.items || []);
      setPaymentModes(pm.data.payment_modes || []);
      setSelected({});
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load reimbursements');
    } finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const selectedIds = Object.keys(selected).filter(k => selected[k]);
  const selectedTotal = pending
    .filter(e => selected[e.id])
    .reduce((s, e) => s + Number(e.amount || 0), 0);
  const allSelected = pending.length > 0 && selectedIds.length === pending.length;
  const toggleAll = () => {
    if (allSelected) setSelected({});
    else setSelected(Object.fromEntries(pending.map(e => [e.id, true])));
  };

  return (
    <div className="space-y-4" data-testid="reimbursements-view">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <h3 className="text-sm font-semibold text-wt-text-primary">Pending Reimbursements</h3>
          {selectedIds.length > 0 && (
            <p className="text-xs text-wt-text-muted mt-1" data-testid="bulk-selection-summary">
              {selectedIds.length} selected · Total {fmtMoney(selectedTotal)}
            </p>
          )}
        </div>
        <div className="flex gap-2">
          {selectedIds.length > 0 && (
            <Button onClick={() => setShowBulk(true)} className="bg-wt-gold hover:bg-wt-gold/90 text-black" data-testid="bulk-settle-btn">
              <Send className="w-4 h-4 mr-1" /> Bulk Settle ({selectedIds.length})
            </Button>
          )}
          <Button variant="outline" onClick={load}><RefreshCw className="w-4 h-4 mr-1" /> Refresh</Button>
        </div>
      </div>
      <div className="bg-wt-paper border border-wt-border rounded-xl overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="text-left text-xs uppercase text-wt-text-muted border-b border-wt-border">
            <tr>
              <th className="px-4 py-3 w-10">
                <Checkbox checked={allSelected} onCheckedChange={toggleAll} data-testid="bulk-select-all" />
              </th>
              <th className="px-4 py-3">Employee</th>
              <th className="px-4 py-3">Title</th>
              <th className="px-4 py-3 text-right">Amount</th>
              <th className="px-4 py-3">Approved</th>
              <th className="px-4 py-3 text-right">Action</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={6} className="px-4 py-8 text-center text-wt-text-muted">Loading…</td></tr>
            ) : pending.length === 0 ? (
              <tr><td colSpan={6} className="px-4 py-8 text-center text-wt-text-muted">No pending reimbursements.</td></tr>
            ) : pending.map((e) => (
              <tr key={e.id} className="border-b border-wt-border last:border-0" data-testid={`reimbursement-row-${e.id}`}>
                <td className="px-4 py-3">
                  <Checkbox
                    checked={!!selected[e.id]}
                    onCheckedChange={(v) => setSelected(s => ({ ...s, [e.id]: !!v }))}
                    data-testid={`bulk-select-${e.id}`}
                  />
                </td>
                <td className="px-4 py-3">{e.requested_by_name}</td>
                <td className="px-4 py-3">{e.title}</td>
                <td className="px-4 py-3 text-right font-mono">{fmtMoney(e.amount)}</td>
                <td className="px-4 py-3 text-wt-text-secondary">{fmtDate(e.approved_at)}</td>
                <td className="px-4 py-3 text-right">
                  <Button size="sm" onClick={() => setActionExpense(e)} className="bg-wt-gold hover:bg-wt-gold/90 text-black" data-testid={`settle-reimb-${e.id}`}>
                    <Send className="w-3 h-3 mr-1" /> Settle
                  </Button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {actionExpense && (
        <ExpenseActionModal
          action="settle"
          expense={actionExpense}
          paymentModes={paymentModes}
          onClose={() => setActionExpense(null)}
          onDone={() => { setActionExpense(null); load(); }}
        />
      )}
      {showBulk && (
        <BulkSettleModal
          expenseIds={selectedIds}
          total={selectedTotal}
          paymentModes={paymentModes}
          onClose={() => setShowBulk(false)}
          onDone={() => { setShowBulk(false); load(); }}
        />
      )}
    </div>
  );
};

const BulkSettleModal = ({ expenseIds, total, paymentModes, onClose, onDone }) => {
  const [paymentModeId, setPaymentModeId] = useState('');
  const [reference, setReference] = useState('');
  const [notes, setNotes] = useState('');
  const [busy, setBusy] = useState(false);

  const run = async () => {
    if (!paymentModeId) { toast.error('Payment mode required'); return; }
    setBusy(true);
    try {
      const r = await axios.post(
        `${API_BASE_URL}/expenses/reimbursements/bulk-settle`,
        { expense_ids: expenseIds, payment_mode_id: paymentModeId, reference, notes },
        { headers: authHeaders() },
      );
      const { settled_count, skipped_count, error_count, total_amount } = r.data;
      toast.success(`Settled ${settled_count} · ${fmtMoney(total_amount)}` + (skipped_count || error_count ? ` · Skipped ${skipped_count}, errors ${error_count}` : ''));
      onDone();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Bulk settle failed');
    } finally { setBusy(false); }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="bulk-settle-modal">
        <DialogHeader><DialogTitle>Bulk Settle {expenseIds.length} Reimbursements</DialogTitle></DialogHeader>
        <div className="space-y-3 text-sm">
          <div className="text-wt-text-secondary">
            Total payout: <span className="text-wt-text-primary font-mono">{fmtMoney(total)}</span>
          </div>
          <div>
            <Label>Payment mode *</Label>
            <Select value={paymentModeId} onValueChange={setPaymentModeId}>
              <SelectTrigger data-testid="bulk-settle-payment-mode"><SelectValue placeholder="Select payment mode" /></SelectTrigger>
              <SelectContent>
                {paymentModes.map(m => (<SelectItem key={m.id} value={m.id}>{m.name}</SelectItem>))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>Reference (optional)</Label>
            <Input value={reference} onChange={e => setReference(e.target.value)} data-testid="bulk-settle-reference" />
          </div>
          <div>
            <Label>Notes (optional)</Label>
            <Textarea value={notes} onChange={e => setNotes(e.target.value)} rows={2} data-testid="bulk-settle-notes" />
          </div>
          <div className="text-xs text-wt-text-muted">
            This posts {expenseIds.length} cash outflows + counter ledger entries in one batch. Already-settled rows in the selection are safely skipped.
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={run} disabled={busy} className="bg-wt-gold hover:bg-wt-gold/90 text-black" data-testid="confirm-bulk-settle-btn">
            {busy ? 'Settling…' : `Settle ${expenseIds.length} (${fmtMoney(total)})`}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// ────────────────────────────  Categories  ─────────────────────────────────
const CategoriesView = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [editing, setEditing] = useState(null);
  const [showForm, setShowForm] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API_BASE_URL}/expenses/categories?include_inactive=true`, { headers: authHeaders() });
      setItems(r.data.categories || []);
    } catch (e) { toast.error('Failed to load categories'); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const remove = async (cat) => {
    if (!window.confirm(`Deactivate "${cat.name}"?`)) return;
    try {
      await axios.delete(`${API_BASE_URL}/expenses/categories/${cat.id}`, { headers: authHeaders() });
      toast.success('Deactivated');
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  return (
    <div className="space-y-4" data-testid="categories-view">
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-semibold text-wt-text-primary">Expense Categories</h3>
        <Button onClick={() => { setEditing(null); setShowForm(true); }} className="bg-wt-gold hover:bg-wt-gold/90 text-black" data-testid="add-category-btn">
          <Plus className="w-4 h-4 mr-1" /> Add Category
        </Button>
      </div>
      <div className="bg-wt-paper border border-wt-border rounded-xl overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="text-left text-xs uppercase text-wt-text-muted border-b border-wt-border">
            <tr>
              <th className="px-4 py-3">Name</th>
              <th className="px-4 py-3">P&L Group</th>
              <th className="px-4 py-3">Approval</th>
              <th className="px-4 py-3">Attachment</th>
              <th className="px-4 py-3">Active</th>
              <th className="px-4 py-3 text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading ? <tr><td colSpan={6} className="px-4 py-8 text-center text-wt-text-muted">Loading…</td></tr>
              : items.length === 0 ? <tr><td colSpan={6} className="px-4 py-8 text-center text-wt-text-muted">No categories.</td></tr>
              : items.map(c => (
                <tr key={c.id} className="border-b border-wt-border last:border-0">
                  <td className="px-4 py-3 text-wt-text-primary">{c.name}{c.is_default && <Badge className="ml-2 bg-zinc-500/20 text-zinc-300">default</Badge>}</td>
                  <td className="px-4 py-3 text-wt-text-secondary">{c.pnl_group}</td>
                  <td className="px-4 py-3"><span className="text-xs">{c.approval_required ? 'Required' : 'Auto'}</span></td>
                  <td className="px-4 py-3"><span className="text-xs">{c.attachment_required ? 'Required' : 'Optional'}</span></td>
                  <td className="px-4 py-3">{c.active ? <CheckCircle2 className="w-4 h-4 text-emerald-400" /> : <X className="w-4 h-4 text-rose-400" />}</td>
                  <td className="px-4 py-3 text-right">
                    <Button size="icon" variant="ghost" onClick={() => { setEditing(c); setShowForm(true); }}><Pencil className="w-4 h-4" /></Button>
                    {c.active && (
                      <Button size="icon" variant="ghost" onClick={() => remove(c)} className="text-rose-400"><Trash2 className="w-4 h-4" /></Button>
                    )}
                  </td>
                </tr>
              ))}
          </tbody>
        </table>
      </div>
      {showForm && (
        <CategoryFormModal editing={editing} onClose={() => setShowForm(false)} onSaved={() => { setShowForm(false); load(); }} />
      )}
    </div>
  );
};
const CategoryFormModal = ({ editing, onClose, onSaved }) => {
  const [f, setF] = useState({
    name: editing?.name || '',
    description: editing?.description || '',
    pnl_group: editing?.pnl_group || 'Other Expense',
    approval_required: editing?.approval_required ?? true,
    attachment_required: editing?.attachment_required ?? false,
    active: editing?.active ?? true,
  });
  const [saving, setSaving] = useState(false);
  const PNL_GROUPS = ['Delivery Cost','Marketing Expense','Staff Expense','Operations Expense','Warehouse Expense','Administrative Expense','Refund/Compensation','Other Expense'];

  const save = async () => {
    if (!f.name.trim()) { toast.error('Name required'); return; }
    setSaving(true);
    try {
      if (editing) await axios.patch(`${API_BASE_URL}/expenses/categories/${editing.id}`, f, { headers: authHeaders() });
      else await axios.post(`${API_BASE_URL}/expenses/categories`, f, { headers: authHeaders() });
      toast.success(editing ? 'Updated' : 'Created');
      onSaved();
    } catch (e) { toast.error(e.response?.data?.detail || 'Save failed'); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="category-form-modal">
        <DialogHeader><DialogTitle>{editing ? 'Edit Category' : 'New Category'}</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div><Label>Name *</Label><Input value={f.name} onChange={e => setF({...f, name: e.target.value})} data-testid="category-name" /></div>
          <div><Label>P&L Group</Label>
            <Select value={f.pnl_group} onValueChange={v => setF({...f, pnl_group: v})}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>{PNL_GROUPS.map(g => <SelectItem key={g} value={g}>{g}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div><Label>Description</Label><Textarea value={f.description} onChange={e => setF({...f, description: e.target.value})} rows={2} /></div>
          <div className="flex items-center justify-between"><Label>Approval required</Label><Switch checked={f.approval_required} onCheckedChange={v => setF({...f, approval_required: v})} /></div>
          <div className="flex items-center justify-between"><Label>Attachment required</Label><Switch checked={f.attachment_required} onCheckedChange={v => setF({...f, attachment_required: v})} /></div>
          <div className="flex items-center justify-between"><Label>Active</Label><Switch checked={f.active} onCheckedChange={v => setF({...f, active: v})} /></div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={save} disabled={saving} className="bg-wt-gold text-black" data-testid="category-save-btn">{saving ? '…' : (editing ? 'Update' : 'Create')}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// ────────────────────────────  Payment Modes  ──────────────────────────────
const PaymentModesView = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [editing, setEditing] = useState(null);
  const [showForm, setShowForm] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API_BASE_URL}/expenses/payment-modes?include_inactive=true`, { headers: authHeaders() });
      setItems(r.data.payment_modes || []);
    } catch (e) { toast.error('Failed to load payment modes'); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const remove = async (m) => {
    if (!window.confirm(`Deactivate "${m.name}"?`)) return;
    try {
      await axios.delete(`${API_BASE_URL}/expenses/payment-modes/${m.id}`, { headers: authHeaders() });
      toast.success('Deactivated'); load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  return (
    <div className="space-y-4" data-testid="payment-modes-view">
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-semibold text-wt-text-primary">Payment Modes</h3>
        <Button onClick={() => { setEditing(null); setShowForm(true); }} className="bg-wt-gold text-black" data-testid="add-payment-mode-btn">
          <Plus className="w-4 h-4 mr-1" /> Add
        </Button>
      </div>
      <div className="bg-wt-paper border border-wt-border rounded-xl overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="text-left text-xs uppercase text-wt-text-muted border-b border-wt-border">
            <tr>
              <th className="px-4 py-3">Name</th>
              <th className="px-4 py-3">Type</th>
              <th className="px-4 py-3 text-right">Opening</th>
              <th className="px-4 py-3 text-right">Current</th>
              <th className="px-4 py-3">Affects Balance</th>
              <th className="px-4 py-3">Active</th>
              <th className="px-4 py-3 text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading ? <tr><td colSpan={7} className="px-4 py-8 text-center text-wt-text-muted">Loading…</td></tr>
              : items.length === 0 ? <tr><td colSpan={7} className="px-4 py-8 text-center text-wt-text-muted">No payment modes.</td></tr>
              : items.map(m => (
                <tr key={m.id} className="border-b border-wt-border last:border-0">
                  <td className="px-4 py-3 text-wt-text-primary">{m.name}</td>
                  <td className="px-4 py-3 capitalize text-wt-text-secondary">{m.type}</td>
                  <td className="px-4 py-3 text-right font-mono">{fmtMoney(m.opening_balance)}</td>
                  <td className="px-4 py-3 text-right font-mono">{fmtMoney(m.current_balance)}</td>
                  <td className="px-4 py-3">{m.affects_company_balance ? 'Yes' : 'No'}</td>
                  <td className="px-4 py-3">{m.active ? <CheckCircle2 className="w-4 h-4 text-emerald-400" /> : <X className="w-4 h-4 text-rose-400" />}</td>
                  <td className="px-4 py-3 text-right">
                    <Button size="icon" variant="ghost" onClick={() => { setEditing(m); setShowForm(true); }}><Pencil className="w-4 h-4" /></Button>
                    {m.active && (
                      <Button size="icon" variant="ghost" onClick={() => remove(m)} className="text-rose-400"><Trash2 className="w-4 h-4" /></Button>
                    )}
                  </td>
                </tr>
              ))}
          </tbody>
        </table>
      </div>
      {showForm && (
        <PaymentModeFormModal editing={editing} onClose={() => setShowForm(false)} onSaved={() => { setShowForm(false); load(); }} />
      )}
    </div>
  );
};
const PaymentModeFormModal = ({ editing, onClose, onSaved }) => {
  const [f, setF] = useState({
    name: editing?.name || '',
    type: editing?.type || 'cash',
    opening_balance: editing?.opening_balance ?? 0,
    affects_company_balance: editing?.affects_company_balance ?? true,
    active: editing?.active ?? true,
  });
  const [saving, setSaving] = useState(false);

  const save = async () => {
    if (!f.name.trim()) { toast.error('Name required'); return; }
    setSaving(true);
    try {
      const payload = { ...f, opening_balance: Number(f.opening_balance) || 0 };
      if (editing) await axios.patch(`${API_BASE_URL}/expenses/payment-modes/${editing.id}`, payload, { headers: authHeaders() });
      else await axios.post(`${API_BASE_URL}/expenses/payment-modes`, payload, { headers: authHeaders() });
      toast.success(editing ? 'Updated' : 'Created');
      onSaved();
    } catch (e) { toast.error(e.response?.data?.detail || 'Save failed'); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="payment-mode-form-modal">
        <DialogHeader><DialogTitle>{editing ? 'Edit Payment Mode' : 'New Payment Mode'}</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div><Label>Name *</Label><Input value={f.name} onChange={e => setF({...f, name: e.target.value})} data-testid="pm-name" /></div>
          <div><Label>Type</Label>
            <Select value={f.type} onValueChange={v => setF({...f, type: v})}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                {['cash','bank','card','wallet','other'].map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div><Label>Opening Balance</Label><Input type="number" step="0.01" value={f.opening_balance} onChange={e => setF({...f, opening_balance: e.target.value})} /></div>
          <div className="flex items-center justify-between"><Label>Affects company balance</Label><Switch checked={f.affects_company_balance} onCheckedChange={v => setF({...f, affects_company_balance: v})} /></div>
          <div className="flex items-center justify-between"><Label>Active</Label><Switch checked={f.active} onCheckedChange={v => setF({...f, active: v})} /></div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={save} disabled={saving} className="bg-wt-gold text-black" data-testid="pm-save-btn">{saving ? '…' : (editing ? 'Update' : 'Create')}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};


// ────────────────────────────  Reports  ────────────────────────────────────
const REPORT_GROUPS = [
  { value: 'category', label: 'Category' },
  { value: 'pnl_group', label: 'P&L Group' },
  { value: 'employee', label: 'Employee' },
  { value: 'paid_by', label: 'Paid By' },
  { value: 'status', label: 'Status' },
  { value: 'payment_mode', label: 'Payment Mode' },
];

const ReportsView = () => {
  const today = new Date().toISOString().slice(0, 10);
  const monthStart = today.slice(0, 8) + '01';
  const [filters, setFilters] = useState({
    date_from: monthStart, date_to: today, group_by: 'category', paid_by: '',
  });
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { ...filters };
      if (!params.paid_by) delete params.paid_by;
      const r = await axios.get(`${API_BASE_URL}/expenses/reports/summary`, { params, headers: authHeaders() });
      setData(r.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load report');
    } finally { setLoading(false); }
  }, [filters]);
  useEffect(() => { load(); }, [load]);

  const downloadCsv = () => {
    const token = localStorage.getItem('token');
    const qs = new URLSearchParams({
      date_from: filters.date_from || '',
      date_to: filters.date_to || '',
      ...(filters.paid_by ? { paid_by: filters.paid_by } : {}),
    }).toString();
    // Use fetch + blob so we can attach Authorization header
    fetch(`${API_BASE_URL}/expenses/reports/export.csv?${qs}`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then(async (res) => {
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const blob = await res.blob();
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `expenses_${filters.date_from || 'all'}_${filters.date_to || 'all'}.csv`;
        document.body.appendChild(a);
        a.click();
        a.remove();
        URL.revokeObjectURL(url);
      })
      .catch((e) => toast.error('Export failed: ' + e.message));
  };

  return (
    <div className="space-y-4" data-testid="reports-view">
      {/* Filters */}
      <div className="bg-wt-paper border border-wt-border rounded-xl p-3 flex flex-wrap items-end gap-2">
        <div>
          <Label className="text-xs">From</Label>
          <DateInput  value={filters.date_from} onChange={e => setFilters({ ...filters, date_from: e.target.value })} max={filters.date_to || undefined} className="w-[150px]" data-testid="report-date-from" />
        </div>
        <div>
          <Label className="text-xs">To</Label>
          <DateInput  value={filters.date_to} onChange={e => setFilters({ ...filters, date_to: e.target.value })} min={filters.date_from || undefined} className="w-[150px]" data-testid="report-date-to" />
        </div>
        <div>
          <Label className="text-xs">Group By</Label>
          <Select value={filters.group_by} onValueChange={(v) => setFilters({ ...filters, group_by: v })}>
            <SelectTrigger className="w-[160px]" data-testid="report-group-by"><SelectValue /></SelectTrigger>
            <SelectContent>
              {REPORT_GROUPS.map(g => (<SelectItem key={g.value} value={g.value}>{g.label}</SelectItem>))}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label className="text-xs">Paid By</Label>
          <Select value={filters.paid_by || 'any'} onValueChange={(v) => setFilters({ ...filters, paid_by: v === 'any' ? '' : v })}>
            <SelectTrigger className="w-[140px]" data-testid="report-paid-by"><SelectValue placeholder="Any" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="any">Any</SelectItem>
              <SelectItem value="company">Company</SelectItem>
              <SelectItem value="employee">Employee</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <Button variant="outline" onClick={load} data-testid="report-refresh-btn">
          <RefreshCw className="w-4 h-4 mr-1" /> Run
        </Button>
        <Button onClick={downloadCsv} className="bg-wt-gold hover:bg-wt-gold/90 text-black ml-auto" data-testid="report-export-csv-btn">
          <Download className="w-4 h-4 mr-1" /> Export CSV
        </Button>
      </div>

      {/* Summary */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        <div className="bg-wt-paper border border-wt-border rounded-xl p-4" data-testid="report-grand-total">
          <div className="text-xs uppercase text-wt-text-muted">Grand Total</div>
          <div className="mt-2 text-2xl font-semibold text-wt-text-primary">{fmtMoney(data?.grand_total || 0)}</div>
        </div>
        <div className="bg-wt-paper border border-wt-border rounded-xl p-4" data-testid="report-count">
          <div className="text-xs uppercase text-wt-text-muted">Expense Count</div>
          <div className="mt-2 text-2xl font-semibold text-wt-text-primary">{data?.grand_count || 0}</div>
        </div>
        <div className="bg-wt-paper border border-wt-border rounded-xl p-4">
          <div className="text-xs uppercase text-wt-text-muted">Grouped By</div>
          <div className="mt-2 text-2xl font-semibold text-wt-text-primary capitalize">{(filters.group_by || '—').replace(/_/g, ' ')}</div>
        </div>
      </div>

      {/* Group table */}
      <div className="bg-wt-paper border border-wt-border rounded-xl overflow-x-auto">
        <table className="w-full text-sm" data-testid="report-table">
          <thead>
            <tr className="text-left text-xs uppercase tracking-wide text-wt-text-muted border-b border-wt-border">
              <th className="px-4 py-3">{REPORT_GROUPS.find(g => g.value === filters.group_by)?.label || 'Group'}</th>
              <th className="px-4 py-3 text-right">Count</th>
              <th className="px-4 py-3 text-right">Total</th>
              <th className="px-4 py-3 text-right w-1/3">% of Total</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={4} className="px-4 py-8 text-center text-wt-text-muted">Loading…</td></tr>
            ) : !data?.groups || data.groups.length === 0 ? (
              <tr><td colSpan={4} className="px-4 py-8 text-center text-wt-text-muted">No data for the selected period.</td></tr>
            ) : (
              data.groups.map((g, i) => {
                const pct = (data.grand_total ? (g.total / data.grand_total) * 100 : 0);
                return (
                  <tr key={`${g.key}-${i}`} className="border-b border-wt-border last:border-0" data-testid={`report-row-${i}`}>
                    <td className="px-4 py-3 text-wt-text-primary capitalize">{(g.label || g.key || '—').toString().replace(/_/g, ' ')}</td>
                    <td className="px-4 py-3 text-right text-wt-text-secondary">{g.count}</td>
                    <td className="px-4 py-3 text-right font-mono text-wt-text-primary">{fmtMoney(g.total)}</td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        <div className="flex-1 h-2 bg-wt-bg/60 rounded overflow-hidden">
                          <div className="h-full bg-wt-gold/70" style={{ width: `${pct.toFixed(1)}%` }} />
                        </div>
                        <span className="text-xs text-wt-text-muted font-mono w-12 text-right">{pct.toFixed(1)}%</span>
                      </div>
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};
